import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { productService, categoryService, paramService } from '@/services/storage';
import type { Product, ProductParam, ProductCategory } from '@/types';
import { toast } from 'sonner';

export default function AdminProductEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [params, setParams] = useState<ProductParam[]>([]);
  const [formData, setFormData] = useState<Partial<Product>>({
    model: '',
    categoryId: '',
    description: '',
    parameters: {},
    applications: [],
    packages: [],
  });

  useEffect(() => {
    const loadCategories = async () => {
      setCategories(await categoryService.getAll());
    };
    loadCategories();
  }, []);

  useEffect(() => {
    const loadProduct = async () => {
      if (isEdit && id) {
        const product = await productService.getById(id);
        if (product) {
          setFormData(product);
          setParams(await paramService.getByCategory(product.categoryId));
        }
      }
    };
    loadProduct();
  }, [id, isEdit]);

  const handleCategoryChange = async (categoryId: string) => {
    setFormData((prev) => ({
      ...prev,
      categoryId,
      parameters: {},
    }));
    setParams(await paramService.getByCategory(categoryId));
  };

  const handleParamChange = (key: string, value: string | number | boolean) => {
    setFormData((prev) => ({
      ...prev,
      parameters: {
        ...prev.parameters,
        [key]: value,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.model || !formData.categoryId) {
      toast.error('请填写必填项');
      return;
    }

    const now = new Date().toISOString();
    const productData: Product = {
      id: isEdit ? id! : Date.now().toString(),
      model: formData.model!,
      categoryId: formData.categoryId!,
      description: formData.description || '',
      parameters: formData.parameters || {},
      applications: formData.applications || [],
      packages: formData.packages || [],
      createdAt: isEdit ? formData.createdAt || now : now,
      updatedAt: now,
    };

    if (isEdit) {
      await productService.update(id!, productData);
      toast.success('产品已更新');
    } else {
      await productService.add(productData);
      toast.success('产品已添加');
    }

    navigate('/admin/products');
  };

  const renderParamInput = (param: ProductParam) => {
    const value = formData.parameters?.[param.key];

    switch (param.type) {
      case 'boolean':
        return (
          <select
            className="w-full border rounded-md px-3 py-2"
            value={String(value || '')}
            onChange={(e) => handleParamChange(param.key, e.target.value === 'true')}
          >
            <option value="">请选择</option>
            <option value="true">是</option>
            <option value="false">否</option>
          </select>
        );
      case 'number':
        return (
          <Input
            type="number"
            value={typeof value === 'number' ? value : ''}
            onChange={(e) => handleParamChange(param.key, Number(e.target.value))}
            placeholder={`请输入${param.label}`}
          />
        );
      default:
        return (
          <Input
            type="text"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => handleParamChange(param.key, e.target.value)}
            placeholder={`请输入${param.label}`}
          />
        );
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate('/admin/products')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-3xl font-bold">
            {isEdit ? '编辑产品' : '添加产品'}
          </h1>
        </div>
        <Button onClick={handleSubmit}>
          <Save className="mr-2 h-5 w-5" />
          保存
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Basic Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-6">基本信息</h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="model">
                      产品型号 <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="model"
                      value={formData.model}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, model: e.target.value }))
                      }
                      placeholder="请输入产品型号，如HP1117"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="category">
                      产品分类 <span className="text-red-500">*</span>
                    </Label>
                    <select
                      id="category"
                      className="w-full border rounded-md px-3 py-2"
                      value={formData.categoryId}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      required
                    >
                      <option value="">请选择分类</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="description">产品描述</Label>
                    <textarea
                      id="description"
                      className="w-full border rounded-md px-3 py-2"
                      rows={4}
                      value={formData.description}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, description: e.target.value }))
                      }
                      placeholder="请输入产品描述"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Parameters */}
            {params.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold mb-6">产品参数</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    {params.map((param) => (
                      <div key={param.key}>
                        <Label>
                          {param.label}
                          {param.unit && (
                            <span className="text-gray-400 text-sm ml-1">({param.unit})</span>
                          )}
                        </Label>
                        {renderParamInput(param)}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div>
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold mb-6">其他信息</h2>

                <div className="space-y-4">
                  <div>
                    <Label>封装类型</Label>
                    <Input
                      placeholder="如SOT23-5, DFN1*1-4L"
                      value={formData.packages?.join(', ') || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          packages: e.target.value.split(',').map((s) => s.trim()),
                        }))
                      }
                    />
                  </div>

                  <div>
                    <Label>应用领域</Label>
                    <Input
                      placeholder="如TWS, 手机, 家电"
                      value={formData.applications?.join(', ') || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          applications: e.target.value.split(',').map((s) => s.trim()),
                        }))
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}
